import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Typography, Menu, Col, Dropdown, Row } from "antd";
import { Fragment } from "react/jsx-runtime";
import { journalsQueries } from "@/entities/journal";
import { useValidateTitle } from "@/features/journal-title-validation";
import { BasketIcon, MoreOneIcon } from "@/shared/assets/svg/icons";
import { fallbackString, formatDate } from "@/shared/utils";
import { ProjectParticipantInfo } from "@entities/project";
import { ParticipantsInfiniteScroll } from "@features/participants-infinite-scroll";
import { Can } from "@shared/lib/casl";
import { useCoordinator } from "@shared/lib/hooks";
import { useGetRepresentative } from "../../api";
import {
  useCreateParticipant,
  useDeleteParticipant,
  useTitleAmendmentMode,
} from "../../model";
import { CollapsibleSection } from "../collapsible-section";
import { createValidationErrorsGetter } from "../journals-title/model/get-validation-errors-for-section";

export const DocumentationContractorCompanyTableNew = () => {
  const { titleId } = useCoordinator();
  const { projectId = "" } = useCoordinator();
  const { data, isFetching } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );

  const { errorList } = useValidateTitle();

  const validationErrors = useMemo(() => {
    return errorList.filter((error) => [33, 30].includes(error.id));
  }, [errorList]);

  const getValidationErrorsForSection = useMemo(
    () => createValidationErrorsGetter(errorList),
    [errorList],
  );

  const titleStatus =
    data?.mainJournal?.titleJournal?.status === "NEW" &&
    // @ts-expect-error versionNumber отсутствует в openapi типе TitleJournalViewDto
    !data?.mainJournal?.titleJournal?.versionNumber;
  const { isNewAmendment } = useTitleAmendmentMode();
  const canAddData = titleStatus || isNewAmendment;

  const { representative: projectDocumentationContractor } =
    useGetRepresentative("projectDocumentationContractor");
  const company = projectDocumentationContractor?.company;
  const sroList = company?.sro || [];

  const { mutate: createParticipant, isPending: createParticipantPending } =
    useCreateParticipant({ titleId });

  const { mutate: deleteParticipant } = useDeleteParticipant({ titleId });

  const onPartySelect = (participant: ProjectParticipantInfo) => {
    if (participant) {
      createParticipant({
        participantType: "projectDocumentationContractor",
        companyId: participant.company.companyId,
        projectParticipantId: participant.projectParticipantId,
      });
    }
  };

  const handleDelete = () => {
    deleteParticipant({
      participantType: "projectDocumentationContractor",
      projectParticipantId:
        projectDocumentationContractor?.projectParticipantId ?? "",
    });
  };

  const menu = (
    <Menu>
      <Menu.Item key="delete" icon={<BasketIcon />} onClick={handleDelete}>
        Удалить
      </Menu.Item>
    </Menu>
  );
  return (
    <CollapsibleSection
      title="Лицо, осуществляющее подготовку проектной документации"
      spinning={isFetching}
      validationErrors={validationErrors}
      sectionId="documentation-contractor-company"
      getValidationErrorsForSection={getValidationErrorsForSection}
      isEmpty={!company}
    >
      <>
        {company ? (
          <div>
            <div className="journal-title-block-header journal-title-block-header--bottom-spaced">
              <Typography.Title level={5} style={{ fontWeight: 400 }}>
                {fallbackString(company?.shortName || company?.fullName)}
              </Typography.Title>
              {titleStatus && !isNewAmendment ? (
                <Can I="edit" a="title_journals">
                  <Dropdown overlay={menu} trigger={["click"]}>
                    <span className="journal-title-menu-trigger">
                      <MoreOneIcon width={24} height={24} />
                    </span>
                  </Dropdown>
                </Can>
              ) : null}
            </div>

            {company ? (
              <Row gutter={[16, 16]}>
                <Fragment>
                  <Col span={8} style={{ color: "#888888" }}>
                    ИНН
                  </Col>
                  <Col span={16}>{fallbackString(company?.inn)}</Col>
                </Fragment>
                <Fragment>
                  <Col span={8} style={{ color: "#888888" }}>
                    КПП
                  </Col>
                  <Col span={16}>{fallbackString(company?.kpp)}</Col>
                </Fragment>
                <Fragment>
                  <Col span={8} style={{ color: "#888888" }}>
                    ОГРН
                  </Col>
                  <Col span={16}>{fallbackString(company?.ogrn)}</Col>
                </Fragment>
                <Fragment>
                  <Col span={8} style={{ color: "#888888" }}>
                    Контакты
                  </Col>
                  <Col span={16}>
                    {fallbackString(
                      [company?.contacts?.phone, company?.contacts?.email]
                        .filter(Boolean)
                        .join(", "),
                    )}
                  </Col>
                </Fragment>
                <Fragment>
                  <Col span={8} style={{ color: "#888888" }}>
                    Адрес
                  </Col>
                  <Col span={16}>
                    {fallbackString(company?.addresses?.legal?.fullAddress)}
                  </Col>
                </Fragment>
              </Row>
            ) : null}

            {company && (
              <>
                {sroList?.length ? (
                  sroList.map((sro) => (
                    <Fragment key={sro.sroId}>
                      <Typography.Title
                        level={5}
                        style={{
                          marginTop: "24px",
                          fontWeight: 400,
                          marginBottom: 24,
                        }}
                      >
                        СРО{" "}
                        {sro.sroType === "ENGINEERING"
                          ? "Проектирование"
                          : "Строительство"}
                      </Typography.Title>
                      <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
                        <Fragment>
                          <Col span={8} style={{ color: "#888888" }}>
                            Номер
                          </Col>
                          <Col span={16}>{fallbackString(sro.sroNumber)}</Col>
                        </Fragment>
                        <Fragment>
                          <Col span={8} style={{ color: "#888888" }}>
                            Дата
                          </Col>
                          <Col span={16}>
                            {fallbackString(formatDate(sro.sroDate))}
                          </Col>
                        </Fragment>
                        <Fragment>
                          <Col span={8} style={{ color: "#888888" }}>
                            Выдано
                          </Col>
                          <Col span={16}>
                            {fallbackString(sro.sroIsserName)}
                          </Col>
                        </Fragment>
                        <Fragment>
                          <Col span={8} style={{ color: "#888888" }}>
                            ИНН
                          </Col>
                          <Col span={16}>{fallbackString(sro.sroIsserInn)}</Col>
                        </Fragment>
                        <Fragment>
                          <Col span={8} style={{ color: "#888888" }}>
                            ОГРН
                          </Col>
                          <Col span={16}>
                            {fallbackString(sro.sroIsserOgrn)}
                          </Col>
                        </Fragment>
                      </Row>
                    </Fragment>
                  ))
                ) : (
                  <>
                    <Typography.Title
                      level={5}
                      style={{
                        marginTop: "24px",
                        fontWeight: 400,
                        marginBottom: 24,
                      }}
                    >
                      СРО Строительство
                    </Typography.Title>
                    <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
                      <Fragment>
                        <Col span={8} style={{ color: "#888888" }}>
                          Номер
                        </Col>
                        <Col span={16}>{fallbackString()}</Col>
                      </Fragment>
                      <Fragment>
                        <Col span={8} style={{ color: "#888888" }}>
                          Дата
                        </Col>
                        <Col span={16}>{fallbackString()}</Col>
                      </Fragment>
                      <Fragment>
                        <Col span={8} style={{ color: "#888888" }}>
                          Выдано
                        </Col>
                        <Col span={16}>{fallbackString()}</Col>
                      </Fragment>
                      <Fragment>
                        <Col span={8} style={{ color: "#888888" }}>
                          ИНН
                        </Col>
                        <Col span={16}>{fallbackString()}</Col>
                      </Fragment>
                      <Fragment>
                        <Col span={8} style={{ color: "#888888" }}>
                          ОГРН
                        </Col>
                        <Col span={16}>{fallbackString()}</Col>
                      </Fragment>
                    </Row>
                    <Typography.Title
                      level={5}
                      style={{
                        marginTop: "24px",
                        fontWeight: 400,
                        marginBottom: 24,
                      }}
                    >
                      СРО Проектирование
                    </Typography.Title>
                    <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
                      <Fragment>
                        <Col span={8} style={{ color: "#888888" }}>
                          Номер
                        </Col>
                        <Col span={16}>{fallbackString()}</Col>
                      </Fragment>
                      <Fragment>
                        <Col span={8} style={{ color: "#888888" }}>
                          Дата
                        </Col>
                        <Col span={16}>{fallbackString()}</Col>
                      </Fragment>
                      <Fragment>
                        <Col span={8} style={{ color: "#888888" }}>
                          Выдано
                        </Col>
                        <Col span={16}>{fallbackString()}</Col>
                      </Fragment>
                      <Fragment>
                        <Col span={8} style={{ color: "#888888" }}>
                          ИНН
                        </Col>
                        <Col span={16}>{fallbackString()}</Col>
                      </Fragment>
                      <Fragment>
                        <Col span={8} style={{ color: "#888888" }}>
                          ОГРН
                        </Col>
                        <Col span={16}>{fallbackString()}</Col>
                      </Fragment>
                    </Row>
                  </>
                )}
              </>
            )}
          </div>
        ) : (
          <div>
            {canAddData ? (
              <Can I="edit" a="title_journals">
                <div className="journal-title-org-heading">Организация</div>
                <ParticipantsInfiniteScroll
                  projectId={projectId}
                  onPartySelect={onPartySelect}
                  isLoading={createParticipantPending}
                />
              </Can>
            ) : null}
          </div>
        )}
      </>
    </CollapsibleSection>
  );
};
